import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Archive, Pencil } from "lucide-react";
import { api } from "@/api/client";
import type { ResolvedTopicWithEstimate } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useTopicsStore } from "@/stores/topics";
import { useBrandsStore } from "@/stores/brands";
import { formatModelLabel } from "@/lib/format";
import { cn } from "@/lib/utils";
import { TopicOverviewTab } from "@/components/topics/TopicOverviewTab";
import { TopicPromptsTab } from "@/components/topics/TopicPromptsTab";
import { TopicCompetitorsTab } from "@/components/topics/TopicCompetitorsTab";
import { TopicTrackingTab } from "@/components/topics/TopicTrackingTab";
import { HealthPill, SentimentDelta } from "@/components/topics/shared";
import { FileText } from "lucide-react";

type Tab = "overview" | "prompts" | "competitors" | "tracking";
const ALL_TABS: { id: Tab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "prompts", label: "Prompts" },
  { id: "competitors", label: "Competitors" },
  { id: "tracking", label: "Tracking" },
];

const normalize = (s: string) => s.trim().toLowerCase() || "general";

function trackingChip(resolved: ResolvedTopicWithEstimate): string | null {
  const tr = resolved.tracking;
  if (!tr?.enabled) return null;
  const cadence = tr.cadence || "weekly";
  const models = tr.models.length;
  const blind = resolved.topic.kind === "pulse" ? "" : ` · blind ${tr.blind_enabled ? "on" : "off"}`;
  return `Tracking ${cadence} · ${models} model${models === 1 ? "" : "s"}${blind}`;
}

export function TopicPage() {
  const { ref } = useParams();
  const navigate = useNavigate();
  const refreshTopics = useTopicsStore((s) => s.refresh);

  const [topicId, setTopicId] = useState<number | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [resolved, setResolved] = useState<ResolvedTopicWithEstimate | null>(null);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [renaming, setRenaming] = useState(false);

  // Resolve the URL ref: all-digits is a topic id; otherwise it's a legacy
  // category name we look up and redirect to the canonical id URL.
  useEffect(() => {
    if (!ref) { setNotFound(true); return; }
    if (/^\d+$/.test(ref)) { setTopicId(parseInt(ref)); setNotFound(false); return; }
    let cancelled = false;
    const wanted = normalize(decodeURIComponent(ref));
    api.topics.list()
      .then((topics) => {
        if (cancelled) return;
        const match = topics.find((t) => normalize(t.name) === wanted);
        if (match) navigate(`/app/topics/${match.id}`, { replace: true });
        else setNotFound(true);
      })
      .catch(() => { if (!cancelled) setNotFound(true); });
    return () => { cancelled = true; };
  }, [ref, navigate]);

  useEffect(() => {
    if (topicId === null) return;
    let cancelled = false;
    setResolved(null);
    api.topics.get(topicId)
      .then((r) => { if (!cancelled) { setResolved(r); setError(""); } })
      .catch((e) => { if (!cancelled) setError(e?.message || "Failed to load the topic"); });
    return () => { cancelled = true; };
  }, [topicId, reloadKey]);

  const onMutated = async () => {
    setReloadKey((k) => k + 1);
    await refreshTopics();
  };

  if (notFound) {
    return (
      <EmptyState
        icon={FileText}
        title="Topic not found"
        description="This topic does not exist or has been archived."
        action={<Link to="/app/health"><Button variant="secondary">Back to Brand Health</Button></Link>}
      />
    );
  }
  if (error && !resolved) return <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />;
  if (!resolved) return <Spinner message="Loading topic…" size="lg" />;

  return <TopicSpace resolved={resolved} topicId={topicId!} onMutated={onMutated} renaming={renaming} setRenaming={setRenaming} />;
}

function TopicSpace({ resolved, topicId, onMutated, renaming, setRenaming }: {
  resolved: ResolvedTopicWithEstimate;
  topicId: number;
  onMutated: () => Promise<void>;
  renaming: boolean;
  setRenaming: (v: boolean) => void;
}) {
  const navigate = useNavigate();
  const refreshTopics = useTopicsStore((s) => s.refresh);
  const storeTopics = useTopicsStore((s) => s.topics);
  const brands = useBrandsStore((s) => s.brands);
  const fetchBrands = useBrandsStore((s) => s.fetch);
  const [searchParams, setSearchParams] = useSearchParams();
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  const isPulse = resolved.topic.kind === "pulse";
  const tabs = ALL_TABS.filter((t) => !(isPulse && t.id === "competitors"));

  const rawTab = (searchParams.get("tab") as Tab) || "overview";
  const activeTab: Tab = tabs.some((t) => t.id === rawTab) ? rawTab : "overview";
  const setTab = (tab: Tab) => setSearchParams(tab === "overview" ? {} : { tab }, { replace: true });

  const trackingEnabled = !!resolved.tracking?.enabled;
  const chip = trackingChip(resolved);
  const name = resolved.topic.name.charAt(0).toUpperCase() + resolved.topic.name.slice(1);
  // Field-sentiment value + WoW delta for the header's banded health pill (from
  // the topics list payload, which carries both per topic).
  const summaryRow = storeTopics.find((t) => t.id === topicId);
  const fieldSentiment = summaryRow?.field_sentiment ?? null;
  const fieldSentimentDelta = summaryRow?.field_sentiment_delta ?? null;
  // Competitor roll-call subline — the first few names, "+N more" for the rest.
  const primaryName = brands.find((b) => b.id === resolved.primaryBrandId)?.name ?? "Your brand";
  const compNames = resolved.competitors.map((c) => c.name);
  const rollCall = compNames.slice(0, 3).join(", ");
  const rollCallExtra = compNames.length > 3 ? ` +${compNames.length - 3} more` : "";
  const tabCount: Partial<Record<Tab, number>> = {
    prompts: resolved.prompts.length,
    competitors: resolved.competitors.length,
  };

  const archive = async () => {
    if (!window.confirm(`Archive "${resolved.topic.name}"? It stops appearing in the nav; history is kept.`)) return;
    setArchiving(true);
    setError("");
    try {
      await api.topics.archive(topicId);
      await refreshTopics();
      navigate("/app/health");
    } catch (e: any) {
      setError(e?.message || "Could not archive the topic.");
      setArchiving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="space-y-2">
        <Link to="/app/health" className="inline-flex items-center gap-1 text-[12.5px] font-medium text-slate-500 hover:text-brand-600">
          <ArrowLeft className="w-3.5 h-3.5" /> Brand Health
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-[28px] font-bold tracking-tight text-brand-950 leading-none">{name}</h1>
            {isPulse ? <Badge variant="warning">Pulse</Badge> : (
              <>
                <HealthPill value={fieldSentiment} />
                <SentimentDelta delta={fieldSentimentDelta} suffix=" sentiment WoW" className="text-[13px]" />
              </>
            )}
            {chip && (
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11.5px] font-semibold text-slate-500">
                {chip}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setRenaming(true)}>
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Rename
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={archive}
              disabled={archiving || trackingEnabled}
              title={trackingEnabled ? "Disable tracking before archiving this topic" : undefined}
            >
              <Archive className="w-3.5 h-3.5 mr-1.5" /> Archive
            </Button>
          </div>
        </div>
        <p className="text-[13px] text-slate-500">
          {isPulse
            ? <>How AI talks about {primaryName} on its own — no competitors.</>
            : compNames.length > 0
              ? <>{primaryName} vs <span className="text-slate-600">{rollCall}{rollCallExtra}</span> — everything about this arena lives here.</>
              : <>Add <Link to="?tab=competitors" className="text-brand-600 font-medium hover:underline">competitors</Link> to benchmark {primaryName} against a field.</>}
        </p>
        {resolved.topic.description && <p className="text-[13px] text-slate-500">{resolved.topic.description}</p>}
        {resolved.tracking?.enabled && resolved.tracking.models.length > 0 && (
          <p className="text-[11.5px] text-slate-400">Models: {resolved.tracking.models.map(formatModelLabel).join(", ")}</p>
        )}
        {error && <p className="text-[12.5px] text-red-600">{error}</p>}
      </div>

      {/* Tab bar — underline style */}
      <div className="flex gap-7 border-b border-slate-200 overflow-x-auto">
        {tabs.map((t) => {
          const active = activeTab === t.id;
          const count = tabCount[t.id];
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              role="tab"
              aria-selected={active}
              className={cn(
                "-mb-px border-b-2 pb-2.5 pt-0.5 px-0.5 text-[13.5px] whitespace-nowrap transition-colors",
                active
                  ? "border-brand-600 text-brand-600 font-semibold"
                  : "border-transparent text-slate-500 hover:text-brand-950 font-medium",
              )}
            >
              {t.label}
              {count !== undefined && <span className="ml-1.5 text-slate-400 tabular-nums font-normal">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Active tab */}
      {activeTab === "overview" && (
        <TopicOverviewTab topicId={topicId} resolved={resolved} onMutated={onMutated} onGoToTracking={() => setTab("tracking")} />
      )}
      {activeTab === "prompts" && (
        <TopicPromptsTab topicId={topicId} topicName={resolved.topic.name} resolved={resolved} onMutated={onMutated} />
      )}
      {activeTab === "competitors" && !isPulse && (
        <TopicCompetitorsTab topicId={topicId} resolved={resolved} onMutated={onMutated} />
      )}
      {activeTab === "tracking" && (
        <TopicTrackingTab topicId={topicId} resolved={resolved} onMutated={onMutated} />
      )}

      <RenameModal
        open={renaming}
        current={resolved.topic.name}
        onOpenChange={setRenaming}
        onRename={async (newName) => { await api.topics.update(topicId, { name: newName }); await onMutated(); }}
      />
    </div>
  );
}

function RenameModal({ open, current, onOpenChange, onRename }: {
  open: boolean;
  current: string;
  onOpenChange: (v: boolean) => void;
  onRename: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState(current);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setName(current); setError(""); } }, [open, current]);

  const submit = async () => {
    if (!name.trim()) { setError("Name is required."); return; }
    setSaving(true);
    setError("");
    try {
      await onRename(name.trim());
      onOpenChange(false);
    } catch (e: any) {
      setError(e?.message || "Could not rename the topic.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rename topic</DialogTitle>
          <DialogDescription>Renaming propagates to this topic's prompts, studies, and cohort tags.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="topic-rename">Name</Label>
            <Input id="topic-rename" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }} />
          </div>
          {error && <p className="text-[12.5px] text-red-600">{error}</p>}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving}>{saving ? "Saving…" : "Rename"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
